import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Flex, Heading, Text } from '@chakra-ui/react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error?: Error;
}

/**
 * Last-resort boundary around the whole app: an uncaught render error shows a
 * recovery screen instead of unmounting React into a white page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {};

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <Flex direction="column" align="center" gap={4} py={20} px={6} data-name="error-boundary">
        <Heading size="lg">Something went wrong</Heading>
        <Text color="fg.muted" textAlign="center">
          The page hit an unexpected error. Reload to keep going.
        </Text>
        <Button data-name="error-boundary-reload" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Flex>
    );
  }
}
